import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import type { CustomData } from 'owa-analytics-types';
import type { OwsError } from '../../dataLayer/ows/owsError';
import type { Feature } from './logFeature';

export interface RunWithQoSConfig<T> {
    scenarioName: string;
    feature: Feature;
    operation: Promise<T>;
    isExpectedFailure?: (details: OwsCallDetails) => boolean;
    additionalQoSLogProperty?: CustomData;
}

export function runWithQoS<T>(config: RunWithQoSConfig<T>): Promise<T> {
    const performanceDatapoint = new PerformanceDatapoint(
        `${config.feature}.${config.scenarioName}`
    );
    performanceDatapoint.addCustomData(config.additionalQoSLogProperty);

    return config.operation.then(
        result => {
            performanceDatapoint.end();
            return result;
        },
        (error: OwsError) => {
            performanceDatapoint.endWithError(
                duration => getDatapointStatus(error, duration, config.isExpectedFailure),
                error.isTransformError ? error : error.serviceFetchError || error
            );
            throw error;
        }
    );
}

export interface OwsCallDetails {
    duration: number;
    response: Response;
}

const getDatapointStatus = (
    error: OwsError,
    duration: number,
    isExpectedFailure: (details: OwsCallDetails) => boolean
) => {
    if (error.isTransformError) {
        return DatapointStatus.ClientError;
    }

    if (isExpectedFailure) {
        try {
            const response = error.serviceFetchError?.response || error.response;
            return response && isExpectedFailure({ response, duration })
                ? DatapointStatus.ServerExpectedError
                : DatapointStatus.ServerError;
        } catch (e) {
            error.message = e.message;
            error.stack = e.stack;
            return DatapointStatus.ClientError;
        }
    }

    return DatapointStatus.ServerError;
};
